// Формулы в документе: выключная (номер справа, экспликация, автоматическая запятая) и строчная.
// Щелчок по формуле - правка прямо на месте (MathLive), как в Word; двойной щелчок - окно редактора.
// Enter - готово (курсор после формулы), Esc - готово (формула выделена), стрелки на краю - выход из формулы.
import { useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import type { MathfieldElement } from 'mathlive';
import type { WhereItem } from '../../core/schema/math';
import { useNum, numberingStore } from '../../core/editor/numberingPlugin';
import { ctx as globalCtx } from '../../core/ctx';
import { MathField, getFieldLatex, setFieldLatex } from './MathField';
import { renderTex, toggleKeyboard, TALL_RE } from './ml';
import { canon, hints, breakPoints, breakAt, lineCount, removeBreaks, isBulky, linearizeFractions } from './latex';
import { mathUI, caretAfter, caretBefore, selectNodeAt, knownSymbols } from './state';
import { whereText, rawWhereText, whereEnd } from './where';
import type { FormulaResult } from './FormulaDialog';

/** Описание символа экспликации: текст, формулы в $…$ отрисовываются. */
function Desc({ text, raw }: { text: string; raw?: boolean }) {
  // raw - строка-определение без символа (импорт .docx): как есть, без смены регистра
  const parts = (raw ? rawWhereText(String(text || '')) : whereText(String(text || ''))).split(/(\$[^$]+\$)/);
  return <>{parts.map((p, i) => (p.startsWith('$') && p.endsWith('$') && p.length > 2
    ? <span key={i} dangerouslySetInnerHTML={{ __html: renderTex(p.slice(1, -1)) }} /> : <span key={i}>{p}</span>))}</>;
}

/** Правка формулы окном редактора (двойной щелчок, «Редактор…»). */
export async function openFormulaDialog(p: NodeViewProps, display: boolean) {
  const { node, editor, getPos } = p;
  const pos = typeof getPos === 'function' ? getPos() : undefined;
  const info = display ? numberingStore.get().byId.get(node.attrs.id) : undefined;
  const r = await globalCtx.openDialog<FormulaResult>('math.edit', {
    latex: node.attrs.latex, numbered: display ? node.attrs.numbered : true, where: display ? node.attrs.where || [] : [],
    display, pos, label: info?.label,
  });
  if (!r || pos === undefined) return;
  replaceFormula(editor, pos, node.type.name, r);
}

/** Заменить формулу в pos результатом окна (с переходом строчная <-> выключная). */
export function replaceFormula(editor: any, pos: number, type: string, r: FormulaResult) {
  const { state } = editor;
  const node = state.doc.nodeAt(pos);
  if (!node) return;
  if (type === 'formula' && r.display) {
    editor.view.dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex: r.latex, numbered: r.numbered, where: r.where }));
    return;
  }
  if (type === 'mathInline' && !r.display) {
    editor.view.dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex: r.latex }));
    return;
  }
  if (type === 'formula' && !r.display) {
    // выключная -> строчная: в конец предыдущего абзаца (или новым абзацем)
    const para = state.schema.nodes.paragraph.create(null, state.schema.nodes.mathInline.create({ latex: r.latex }));
    editor.view.dispatch(state.tr.replaceWith(pos, pos + node.nodeSize, para));
    return;
  }
  // строчная -> выключная: абзац делится на «до формулы», формулу и «после формулы»
  const $p = state.doc.resolve(pos);
  const d = $p.depth;
  const para = $p.node(d);
  const start = $p.start(d);
  const before = para.content.cut(0, pos - start);
  const after = para.content.cut(pos - start + node.nodeSize);
  const S = state.schema;
  const blocks: any[] = [];
  if (before.size) blocks.push(S.nodes.paragraph.create(para.attrs, before));
  blocks.push(S.nodes.formula.create({ latex: r.latex, numbered: r.numbered, where: r.where }));
  if (after.size) blocks.push(S.nodes.paragraph.create(null, trimStart(after)));
  editor.view.dispatch(state.tr.replaceWith($p.before(d), $p.after(d), blocks));
}

function trimStart(frag: any) {
  const first = frag.firstChild;
  if (first && first.isText && /^[\s,]+/.test(first.text)) {
    const t = first.text.replace(/^\s+/, '');
    if (!t) return frag.cut(first.nodeSize);
    return frag.replaceChild(0, first.type.schema.text(t, first.marks));
  }
  return frag;
}

// ------------------------------------------------------------------------------------------------ выключная
export function FormulaView(p: NodeViewProps) {
  const { node, selected, updateAttributes, editor, getPos } = p;
  const info = useNum(node.attrs.id);
  const [edit, setEdit] = useState<null | { at: { x: number; y: number } | null }>(null);
  const [draft, setDraft] = useState<string>(node.attrs.latex);
  const [showSrc, setShowSrc] = useState(false);
  const mfRef = useRef<MathfieldElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const pos = () => (typeof getPos === 'function' ? getPos() : undefined);
  const html = useMemo(() => renderTex(node.attrs.latex, true), [node.attrs.latex]);
  const where: WhereItem[] = node.attrs.where || [];
  // фокус ушёл из формулы и её панели (поле, код LaTeX) - правка закончена
  const onBoxBlur = () => setTimeout(() => {
    const a = document.activeElement;
    if (boxRef.current && a && boxRef.current.contains(a)) return;
    commitRef.current('blur');
  }, 0);
  const commitRef = useRef<(how: 'blur') => void>(() => {});

  // открыть для правки сразу после вставки
  useEffect(() => {
    const check = () => { if (mathUI.takePending(pos())) { setDraft(node.attrs.latex); setEdit({ at: null }); } };
    check();
    return mathUI.on(check);
  }, []);
  useEffect(() => { if (!edit) setDraft(node.attrs.latex); }, [node.attrs.latex]);

  const editing = useRef(false);
  editing.current = !!edit;
  const commit = (how: 'enter' | 'escape' | 'blur' | 'forward' | 'backward') => {
    if (!edit || !editing.current) return;
    editing.current = false;
    const latex = canon(mfRef.current ? getFieldLatex(mfRef.current, draft) : draft).latex;
    setEdit(null);
    setShowSrc(false);
    const at = pos();
    if (at === undefined) return;
    if (!latex.trim()) {
      const n = editor.state.doc.nodeAt(at);
      if (n) editor.chain().deleteRange({ from: at, to: at + n.nodeSize }).run();
      if (how !== 'blur') editor.commands.focus();
      return;
    }
    if (latex !== node.attrs.latex) updateAttributes({ latex });
    if (how === 'enter' || how === 'forward') caretAfter(editor, at, true);
    else if (how === 'backward') caretBefore(editor, at, true);
    else if (how === 'escape') selectNodeAt(editor, at);
  };

  commitRef.current = commit;
  const startedAt = useRef(0);
  const start = (e?: React.MouseEvent) => {
    if (edit || !editor.isEditable) return;
    startedAt.current = Date.now();
    setDraft(node.attrs.latex);
    setEdit({ at: e ? { x: e.clientX, y: e.clientY } : null });
  };
  // двойной щелчок - окно полного редактора (первый щелчок уже открыл правку на месте - её закрываем)
  const onDbl = (e: React.MouseEvent) => {
    if (edit && Date.now() - startedAt.current > 800) return;       // двойной щелчок внутри поля - выделение в формуле
    e.preventDefault();
    if (edit) { editing.current = false; setEdit(null); setShowSrc(false); }
    void openFormulaDialog(p, true);
  };

  const next = nextIsFormula(editor, pos(), node.nodeSize);
  const autoComma = (where.length > 0 || next) && !/[,;.]\s*$/.test(node.attrs.latex);
  const cls = 'sto-formula' + (selected ? ' is-selected' : '') + (node.attrs.latex ? '' : ' is-empty') + (edit ? ' is-editing' : '')
    + (TALL_RE.test(node.attrs.latex || '') ? ' is-tall' : '');
  return (
    <NodeViewWrapper className={cls} data-numbered={String(node.attrs.numbered)} data-drag-handle=""
      onDoubleClick={onDbl}>
      <div ref={boxRef} onBlur={edit ? onBoxBlur : undefined} className="mx-box">
      <div className="sto-formula-row" contentEditable={false}>
        <span className="sto-formula-body">
          {edit ? (
            <MathField value={draft} onChange={setDraft} kind="block" autoFocus clickAt={edit.at} selectPlaceholder
              fieldRef={(m) => { mfRef.current = m; }} className="mx-inplace" testid="formula-inplace"
              onEnter={() => commit('enter')} onEscape={() => commit('escape')}
              onMoveOut={(d) => commit(d)} onDeleteEmpty={() => commit('escape')} commit={() => commit('enter')} />
          ) : (
            <span className="sto-formula-math" onClick={start} title="Щелчок - изменить на месте, двойной щелчок - окно редактора"
              dangerouslySetInnerHTML={{ __html: node.attrs.latex ? html : '<span class="muted">Пустая формула - щёлкните и наберите</span>' }} />
          )}
          {!edit && autoComma && <span className="sto-auto-punct" title={where.length ? 'Запятая перед «где» ставится автоматически (4.6.1.7)' : 'Формулы подряд разделяются запятой автоматически (4.6.1.9)'}>,</span>}
        </span>
        <span className="sto-formula-num" title={node.attrs.numbered ? 'Номер формулы - автоматически' : 'Без номера'}>{node.attrs.numbered ? (info?.label || '') : ''}</span>
      </div>
      {edit && <FormulaBar p={p} draft={draft} setDraft={(v) => { setDraft(v); if (mfRef.current) setFieldLatex(mfRef.current, v); }} mf={mfRef}
        showSrc={showSrc} setShowSrc={setShowSrc} commit={() => commit('enter')} />}
      </div>
      {where.length > 0 && (
        <div className="sto-where" contentEditable={false} onClick={() => editWhere(p)} title="Щелчок - изменить экспликацию">
          {where.map((w, i) => (
            <div key={i} className="sto-where-row">
              {i === 0 ? 'где ' : ''}{w.sym
                ? <><span dangerouslySetInnerHTML={{ __html: renderTex(w.sym) }} /> – <Desc text={w.desc} /></>
                : <Desc text={w.desc} raw />}{whereEnd(w, i === where.length - 1)}
            </div>
          ))}
        </div>
      )}
    </NodeViewWrapper>
  );
}

function nextIsFormula(editor: any, pos: number | undefined, size: number): boolean {
  if (pos === undefined) return false;
  try {
    const n = editor.state.doc.resolve(pos + size).nodeAfter;
    return !!n && n.type.name === 'formula' && !!n.attrs.latex;
  } catch { return false; }
}

/** Экспликация отдельным окном. */
export async function editWhere(p: NodeViewProps) {
  const { node, editor, getPos } = p;
  const pos = typeof getPos === 'function' ? getPos() : undefined;
  const r = await globalCtx.openDialog<WhereItem[]>('math.where', { latex: node.attrs.latex, where: node.attrs.where || [], pos });
  if (!r || pos === undefined) return;
  const n = editor.state.doc.nodeAt(pos);
  if (n) editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, where: r }));
}

/** Панель под формулой во время правки на месте. */
function FormulaBar({ p, draft, setDraft, mf, showSrc, setShowSrc, commit }: {
  p: NodeViewProps; draft: string; setDraft: (v: string) => void; mf: React.MutableRefObject<MathfieldElement | null>;
  showSrc: boolean; setShowSrc: (b: boolean) => void; commit: () => void;
}) {
  const { node, updateAttributes } = p;
  const [brk, setBrk] = useState(false);
  const c = canon(draft);
  const hs = hints(c.latex, { numbered: node.attrs.numbered });
  const pts = breakPoints(c.latex).sort((a, b) => a.prio - b.prio || a.index - b.index).slice(0, 8);
  const lines = lineCount(c.latex);
  const keep = (e: React.MouseEvent) => e.preventDefault();
  // клавиши поля LaTeX ловим обработчиком на самом поле (до ProseMirror на view.dom, который стоит ниже по пути
  // всплытия, чем корень React): Ctrl+Enter - «Готово», а не разрыв страницы
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const srcKeys = useRef<((e: KeyboardEvent) => void) | null>(null);
  if (!srcKeys.current) {
    srcKeys.current = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); commitRef.current(); }
      else if (e.key === 'Escape') { e.preventDefault(); commitRef.current(); }
    };
  }
  return (
    <div className="mx-bar" contentEditable={false} onMouseDown={keep} data-testid="formula-bar">
      <button type="button" className={node.attrs.numbered ? 'is-on' : ''} title="Номер формулы у правого поля (4.6.1.6); без номера - строка расчёта (4.6.1.8)"
        onClick={() => updateAttributes({ numbered: !node.attrs.numbered })} data-testid="bar-num"># Номер</button>
      <button type="button" title="Экспликация «где …» (4.6.1.7)" onClick={() => { commit(); void editWhere(p); }} data-testid="bar-where">
        где…{(node.attrs.where || []).length ? ` (${node.attrs.where.length})` : ''}</button>
      <span className="mx-bar-drop">
        <button type="button" title="Перенос длинной формулы (4.6.2.5)" onClick={() => setBrk(!brk)} disabled={!pts.length && lines < 2}>↵ Перенос{lines > 1 ? ` (${lines})` : ''}</button>
        {brk && (
          <span className="mx-brk-menu" onMouseLeave={() => setBrk(false)}>
            {lines > 1 && <button type="button" onClick={() => { setDraft(removeBreaks(c.latex)); setBrk(false); }}>Убрать переносы</button>}
            {pts.map((b) => <button key={b.index} type="button" onClick={() => { setDraft(breakAt(c.latex, b.index)); setBrk(false); }}>
              <span className={'mx-prio mx-prio-' + b.prio}>{b.prio === 1 ? 'лучше' : b.prio === 2 ? 'можно' : 'крайний случай'}</span> {b.label}</button>)}
          </span>
        )}
      </span>
      <button type="button" className={showSrc ? 'is-on' : ''} title="Код LaTeX" onClick={() => setShowSrc(!showSrc)}>LaTeX</button>
      <button type="button" title="Экранная клавиатура" onClick={() => toggleKeyboard(mf.current)}>⌨</button>
      <button type="button" title="Окно редактора: палитры, вставка из Word, экспликация" onClick={() => { commit(); void openFormulaDialog(p, true); }}>Редактор…</button>
      <span className="mx-grow" />
      {hs.filter((h) => h.sev !== 'I').slice(0, 1).map((h) => <span key={h.code} className={'mx-bar-hint mx-hint-' + h.sev} title={h.msg}>⚠ {h.msg}</span>)}
      <button type="button" className="mx-done" onClick={commit} data-testid="bar-done" title="Enter или Ctrl+Enter - готово, Esc - готово и выделить, Tab - к пустому месту">✓ Готово</button>
      {showSrc && (
        <textarea className="mono mx-bar-src" rows={2} value={draft} spellCheck={false}
          onChange={(e) => setDraft(e.target.value)} onMouseDown={(e) => e.stopPropagation()} data-testid="bar-src"
          ref={(el) => {
            if (el && !el.dataset.shown) {
              el.dataset.shown = '1';
              el.addEventListener('keydown', srcKeys.current!);
              el.scrollIntoView({ block: 'nearest' });
            }
          }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ строчная
export function MathInlineView(p: NodeViewProps) {
  const { node, selected, updateAttributes, editor, getPos } = p;
  const [edit, setEdit] = useState<null | { at: { x: number; y: number } | null }>(null);
  const [draft, setDraft] = useState<string>(node.attrs.latex);
  const mfRef = useRef<MathfieldElement | null>(null);
  const pos = () => (typeof getPos === 'function' ? getPos() : undefined);
  const html = useMemo(() => renderTex(node.attrs.latex), [node.attrs.latex]);

  useEffect(() => {
    const check = () => { if (mathUI.takePending(pos())) { setDraft(node.attrs.latex); setEdit({ at: null }); } };
    check();
    return mathUI.on(check);
  }, []);
  useEffect(() => { if (!edit) setDraft(node.attrs.latex); }, [node.attrs.latex]);

  const editing = useRef(false);
  const startedAt = useRef(0);
  editing.current = !!edit;
  const commit = (how: 'enter' | 'escape' | 'blur' | 'forward' | 'backward') => {
    if (!edit || !editing.current) return;
    editing.current = false;
    const latex = canon(mfRef.current ? getFieldLatex(mfRef.current, draft) : draft).latex;
    setEdit(null);
    const at = pos();
    if (at === undefined) return;
    if (!latex.trim()) {
      editor.chain().deleteRange({ from: at, to: at + node.nodeSize }).run();
      if (how !== 'blur') editor.commands.focus();
      return;
    }
    if (latex !== node.attrs.latex) updateAttributes({ latex });
    if (how === 'enter' || how === 'forward') caretAfter(editor, at, false);
    else if (how === 'backward') caretBefore(editor, at, false);
    else if (how === 'escape') selectNodeAt(editor, at);
  };
  const bulky = isBulky(edit ? canon(draft).latex : node.attrs.latex);
  const toBlock = () => {
    const latex = canon(mfRef.current ? getFieldLatex(mfRef.current, draft) : draft).latex;
    setEdit(null);
    const at = pos();
    if (at !== undefined) replaceFormula(editor, at, 'mathInline', { latex, numbered: true, where: [], display: true });
  };

  return (
    <NodeViewWrapper as="span" className={'sto-math-inline' + (selected ? ' is-selected' : '') + (edit ? ' is-editing' : '') + (bulky ? ' is-bulky' : '')}
      onDoubleClick={(e: React.MouseEvent) => {
        if (edit && Date.now() - startedAt.current > 800) return;
        e.preventDefault();
        if (edit) { editing.current = false; setEdit(null); }
        void openFormulaDialog(p, false);
      }}>
      {edit ? (
        <span className="mx-inline-edit" contentEditable={false}>
          <MathField value={draft} onChange={setDraft} kind="inline" autoFocus clickAt={edit.at} selectPlaceholder
            fieldRef={(m) => { mfRef.current = m; }} className="mx-inplace-inline" testid="math-inline-inplace"
            onEnter={() => commit('enter')} onEscape={() => commit('escape')} onBlur={() => commit('blur')}
            onMoveOut={(d) => commit(d)} onDeleteEmpty={() => commit('escape')} commit={() => commit('enter')} />
          <span className="mx-inline-bar" onMouseDown={(e) => e.preventDefault()}>
            {isBulky(canon(draft).latex) && <span className="mx-hint-E" title="Громоздкую формулу выносят в отдельную строку (4.6.1.1)">громоздкая</span>}
            {isBulky(canon(draft).latex) && /\\frac/.test(draft) && <button type="button" onClick={() => setDraft(linearizeFractions(canon(draft).latex))} title="Дробь через «/»">a/b</button>}
            <button type="button" onClick={toBlock} title="Вынести в отдельную строку с номером">⤓ Отдельной строкой</button>
            <button type="button" onClick={() => toggleKeyboard(mfRef.current)} title="Экранная клавиатура">⌨</button>
            <button type="button" onClick={() => { commit('blur'); void openFormulaDialog(p, false); }}>Редактор…</button>
            <button type="button" className="mx-done" onClick={() => commit('enter')}>✓</button>
          </span>
        </span>
      ) : (
        <span className="sto-math-inline-math" onClick={(e) => { if (editor.isEditable) { startedAt.current = Date.now(); setDraft(node.attrs.latex); setEdit({ at: { x: e.clientX, y: e.clientY } }); } }}
          title={bulky ? 'Громоздкая формула в строке - вынесите в отдельную строку (4.6.1.1)' : 'Щелчок - изменить'}
          dangerouslySetInnerHTML={{ __html: node.attrs.latex ? html : '<span class="muted">формула</span>' }} />
      )}
    </NodeViewWrapper>
  );
}

export { knownSymbols };
